import { CssBaseline } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import buildAdminTheme from "./theme/adminTheme";
import { applyThemeColors } from "./theme/designTokens";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Route, Routes } from "react-router-dom";
import Missing from "./components/404/Missing";
import LoadingBox from "./components/misc/Loading";
import SiteMap from "./components/misc/SiteMap";
import { axiosInstance } from "./config";
import DashBoard from "./screens/adminPanel/DashBoard";
import Blog from "./screens/Blog";
import Category from "./screens/Category";
import ContactUsScreen from "./screens/ContactUsScreen";
import FrontEndLayout from "./screens/FrontEndLayout";
import Home from "./screens/Home";
import HowToOrderScreen from "./screens/HowToOrderScreen";
import OurMedicines from "./screens/OurMedicines";
// import SingleBlog from "./screens/SingleBlog";
import SingleMedicine from "./screens/SingleMedicine";
import SingleMedicineVariation2 from "./screens/SingleMedicineVariation2";
import TestimonialScreen from "./screens/TestimonialScreen";
import Unblock from "./screens/Unblock";

import { HelmetProvider } from "react-helmet-async";
import SingleBlog from "./screens/SingleBlog";
import MaintenanceMode from "./components/MaintenanceMode/MaintenanceMode";
import { getSettings } from "./reduxToolkit/features/adminPanel/webSettings";
import PreviewLeafletPage from "./screens/adminPanel/medicines/components/leaflet/PreviewLeafletPage";
import PreviewBrochurePage from "./screens/adminPanel/medicines/components/brochure/PreviewBrochurePage";
import Generics from "./screens/Generics";
import SingleGeneric from "./screens/SingleGeneric";
import LangGuard from "./components/misc/LangGuard";
import LegacyLangRedirect from "./components/misc/LegacyLangRedirect";
import { getLangs } from "./reduxToolkit/features/adminPanel/misc";
function App() {
  const dispatch = useDispatch();
  const [preLoader, setPreLoader] = useState(false);
  const [policyOpen, setPolicyOpen] = useState(false);
  const [policyName, setPolicyName] = useState("");
  const [adminUrl, setAdminUrl] = useState("");
  const [medicinePageVariation,,] = useState(0);

  const { webSettings } = useSelector((state) => state.webSettings);

  // Apply admin-configured Site Design colors (Website Settings -> Site
  // Design) before children render, so both the storefront's sx-based
  // colors and the admin theme reflect them.
  if (webSettings) {
    applyThemeColors({
      primary: webSettings.themePrimaryColor,
      accent: webSettings.themeAccentColor,
    });
  }
  const adminTheme = useMemo(
    () => buildAdminTheme(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [webSettings?.themePrimaryColor, webSettings?.themeAccentColor]
  );

  // Use Effect
  useEffect(() => {
    getAdminUrl();
    // Warm the langs cache as early as possible so LangGuard/
    // LegacyLangRedirect don't have to wait on it themselves.
    dispatch(getLangs());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setPreLoader(true);
    setTimeout(() => {
      setPreLoader(false);
      if (!webSettings) dispatch(getSettings());
    }, 1000);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getAdminUrl = async () => {
    const res = await axiosInstance.get("/api/adminUrl");
    setAdminUrl(res.data.url);
  };

  // Handle Maintenance Mode
  const [maintenanceMode, setMaintenanceMode] = useState(undefined);
  // const [maintenanceTime, setMaintenanceTime] = useState(0);
  const [maintenanceExpireTime, setMaintenanceExpireTime] = useState(0);

  useEffect(() => {
    if (webSettings) {
      if (webSettings.maintenanceMode) setMaintenanceMode(true);
      else setMaintenanceMode(false);
      // if (webSettings.maintenanceMinute)
      //   setMaintenanceTime(webSettings.maintenanceMinute);
      // else setMaintenanceTime(0);
      if (webSettings.maintenanceExpireTime)
        setMaintenanceExpireTime(webSettings.maintenanceExpireTime);
      else setMaintenanceExpireTime(0);
    }
  }, [webSettings]);

  return (
    <>
      <CssBaseline />
      <HelmetProvider>
        {preLoader || maintenanceMode === undefined ? (
          <LoadingBox w={"100%"} h={"100vh"} />
        ) : !window.location.href.includes(adminUrl) && maintenanceMode ? (
          <MaintenanceMode maintenanceExpireTime={maintenanceExpireTime} />
        ) : (
          adminUrl !== "" && (
            <Routes>
              <Route
                path="/medicines/leaflet"
                element={<PreviewLeafletPage />}
              />
              <Route
                path="/medicines/brochure"
                element={<PreviewBrochurePage />}
              />

              {/* Legacy (pre-language-prefix) storefront URLs — redirect
                  to the same path under the visitor's language alias,
                  so existing bookmarks/search-indexed links keep working. */}
              <Route path="/" element={<LegacyLangRedirect />} />
              <Route path="/our-medicines/*" element={<LegacyLangRedirect />} />
              <Route path="/generics/*" element={<LegacyLangRedirect />} />
              <Route path="/category/*" element={<LegacyLangRedirect />} />
              <Route path="/blog/*" element={<LegacyLangRedirect />} />
              <Route path="/testimonials" element={<LegacyLangRedirect />} />
              <Route path="/contact-us" element={<LegacyLangRedirect />} />
              <Route path="/unblock-request" element={<LegacyLangRedirect />} />
              <Route path="/how-to-order" element={<LegacyLangRedirect />} />
              <Route path="/sitemap" element={<LegacyLangRedirect />} />

              <Route path="/:langAlias" element={<LangGuard />}>
                <Route
                  element={
                    <FrontEndLayout
                      policyOpen={policyOpen}
                      setPolicyOpen={setPolicyOpen}
                      policyName={policyName}
                      setPolicyName={setPolicyName}
                    />
                  }
                >
                  <Route index element={<Home />} />
                  <Route path="our-medicines">
                    <Route index element={<OurMedicines />} />
                    {medicinePageVariation === 0 ? (
                      <Route path=":id" element={<SingleMedicine />} />
                    ) : (
                      <Route
                        path=":id"
                        element={<SingleMedicineVariation2 />}
                      />
                    )}
                  </Route>
                  <Route path="generics">
                    <Route index element={<Generics />} />
                    <Route path=":id" element={<SingleGeneric />} />
                  </Route>
                  <Route path="category/:id" element={<Category />} />
                  <Route path="blog">
                    <Route index element={<Blog />} />
                    <Route path=":id" element={<SingleBlog />} />
                  </Route>
                  <Route path="testimonials" element={<TestimonialScreen />} />
                  <Route path="contact-us" element={<ContactUsScreen />} />
                  <Route path="unblock-request" element={<Unblock />} />
                  <Route
                    path="how-to-order"
                    element={
                      <HowToOrderScreen
                        setPolicyOpen={setPolicyOpen}
                        setPolicyName={setPolicyName}
                      />
                    }
                  />
                  <Route path="sitemap" element={<SiteMap />} />
                  <Route path="*" element={<Missing />} />
                </Route>
              </Route>

              <Route
                path={`/${adminUrl}/*`}
                element={
                  <ThemeProvider theme={adminTheme}>
                    <DashBoard adminUrl={adminUrl} />
                  </ThemeProvider>
                }
              />
            </Routes>
          )
        )}
      </HelmetProvider>
    </>
  );
}

export default App;
